import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Spinner, Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import MediaSelector from '../Media/MediaSelector';
import { createMediaGalleryInstance } from '../../../Models/MediaGallery';
import { faTrash, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LanguageService } from '../../../Services/Axios/Api';
import LanguageSelector from '../../../Components/LanguageSelector/LanguageSelector';
import { availableAnimations } from '../../../Services/Animations/availableAnimations';

const GalleryBuilder = ({id}) => {
    const [galleryInstance, setGalleryInstance] = useState(createMediaGalleryInstance());
    const [loading, setLoading] = useState(false);
    const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    
    // Language states - only used when id is null
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);   
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);
    const [mode, setMode] = useState(galleryInstance.getMode());
    const [animation, setAnimation] = useState(galleryInstance.getAnimation());

    useEffect(() => {
        if (id) {
            // If ID exists, load directly without language management
            loadGalleryData();
        } else {
            // If no ID, load languages first
            loadLanguages();
        }
    }, [id]);

    // Load data after languages are loaded and current language is set (only when no ID)
    useEffect(() => {
        if (!id && isLanguagesLoaded && currentLanguage) {
            loadGalleryData();
        }
    }, [isLanguagesLoaded, currentLanguage, id]);

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
            
            // Set default language from API
            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
            
            console.log('GalleryBuilder: Language loaded:', defaultLanguage);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    };

    const loadGalleryData = async () => {
        try {
            setLoading(true);
            if (id) {
                // Load by ID first (priority)
                console.log('GalleryBuilder: Loading data for ID:', id);
                await galleryInstance.load(id);
            } else {
                // Load by language when no ID
                console.log('GalleryBuilder: Loading data for language:', currentLanguage);
                await galleryInstance.loadByLanguage(currentLanguage);
            }
            setGalleryInstance(galleryInstance);
            setAnimation(galleryInstance.getAnimation());
        } catch (error) {
            console.error('Error loading gallery data:', error);
            toast.error('Failed to load gallery data');
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        // loadGalleryData will be called automatically by the useEffect when currentLanguage changes
    };

    const handleMediaSelect = (selectedPaths) => {
        if (Array.isArray(selectedPaths)) {
            galleryInstance.addImages(selectedPaths);
            setGalleryInstance(galleryInstance);
        }
        setIsMediaSelectorOpen(false);
    };

    const handleRemoveImage = (imageUrl) => {
        const newGalleryInstance = createMediaGalleryInstance();
        newGalleryInstance.setData({
            ...galleryInstance.getData(),
            images: galleryInstance.getImages().filter(img => img !== imageUrl)
        });
        setGalleryInstance(newGalleryInstance);
    };

    const handleTitleChange = (e) => {
        const newGalleryInstance = createMediaGalleryInstance();
        newGalleryInstance.setData({
            ...galleryInstance.getData(),
            title: e.target.value
        });
        setGalleryInstance(newGalleryInstance);
    };

    const handleVisibilityToggle = () => {
        const newGalleryInstance = createMediaGalleryInstance();
        newGalleryInstance.setData({ ...galleryInstance.getData(), isVisible: !galleryInstance.isVisible() });
        setGalleryInstance(newGalleryInstance);
    };

    const handleModeChange = (e) => {
        const newGalleryInstance = createMediaGalleryInstance();
        newGalleryInstance.setData({ ...galleryInstance.getData(), mode: e.target.value });
        setGalleryInstance(newGalleryInstance);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await galleryInstance.setAnimation(animation);
            await galleryInstance.save();
            toast.success('Gallery saved successfully');
        } catch (error) {
            toast.error('Failed to save gallery');
            console.error('Error saving gallery:', error);
        } finally {
            setLoading(false);
        }
    };


    const isVideoFile = (filePath) => {
        return /\.(mp4|mov|avi|mkv|m4v|hevc|mts|m2ts|3gp|webm|wmv|ogg|flv)$/i.test(filePath);
    };
    
    const isImageFile = (filePath) => {
        return /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|raw|tiff|cr2|nef|orf|arw|dng)$/i.test(filePath);
    };
    
    // Note: URLs are now normalized during input (UrlMediaDialog). We render as-is here.
  
    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <h2 className="mb-4">Gallery Builder</h2>
            
            {/* Language Management Card - Only show when no ID */}
            {!id && (
                <Card className="mb-4 language-management-card">
                    <Card.Header className="language-management-header">
                        <Row className="">
                            <Col md={6}>
                                <div className="d-flex align-items-center gap-3">
                                    <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                    <div>
                                        <h6 className="mb-0">Language Management</h6>
                                        <small className="text-muted">
                                            Managing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                        </small>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6} className="d-flex justify-content-end">
                                <LanguageSelector
                                    currentLanguage={currentLanguage}
                                    availableLanguages={availableLanguages}
                                    onLanguageChange={handleLanguageChange}
                                    showBadge={false}
                                    showCopyFromLanguage={false}
                                />
                            </Col>
                        </Row>
                    </Card.Header>
                    
                    {/* Language Alert */}
                    {showLanguageAlert && (
                        <Card.Body className="p-3 language-alert-container">
                            <Alert
                                variant={languageAlertType} 
                                dismissible 
                                onClose={() => setShowLanguageAlert(false)}
                                className="mb-0"
                            >
                                <div className="d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon={faGlobe} />
                                    <div>
                                        {languageAlertType === 'warning' ? (
                                            <>
                                                <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                                {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                            </>
                                        ) : (
                                            <>
                                                <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this page.
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Alert>
                        </Card.Body>
                    )}
                </Card>
            )}
            
            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Gallery Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={galleryInstance.getTitle()}
                            onChange={handleTitleChange}
                            placeholder="Enter gallery title"
                        />
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group>
                        <Form.Label>Display Mode</Form.Label>
                        <Form.Select
                            className='form-control'
                            value={galleryInstance.getMode()}
                            onChange={handleModeChange}
                        >
                            <option value="grid">Grid</option>
                            <option value="carousel">Carousel</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                    <Form.Check
                        type="switch"
                        id="visibility-switch"
                        label="Show Gallery"
                        checked={galleryInstance.isVisible()}
                        onChange={handleVisibilityToggle}
                    />
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Animation Type</Form.Label>
                        <Form.Select
                            value={animation.animationType}
                            onChange={(e) => {
                                const updated = { ...animation, animationType: e.target.value };
                                setAnimation(updated);
                                galleryInstance.setAnimation(updated);
                            }}
                        >
                            {availableAnimations.map((anim) => (
                                <option key={anim} value={anim}>{anim}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Animation Duration (ms)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={animation.animationDuration}
                            onChange={(e) => {
                                const updated = { ...animation, animationDuration: e.target.value };
                                setAnimation(updated);
                                galleryInstance.setAnimation(updated);
                            }}
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Animation Delay (ms)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            value={animation.animationDelay}
                            onChange={(e) => {
                                const updated = { ...animation, animationDelay: e.target.value };
                                setAnimation(updated);
                                galleryInstance.setAnimation(updated);
                            }}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Button 
                        variant="success" 
                        onClick={() => setIsMediaSelectorOpen(true)}
                    >
                        Add Images
                    </Button>
                </Col>
            </Row>

            <Row className="g-4">
                {galleryInstance.getImages().map((imageUrl, index) => (
                    <Col key={index} xs={6} sm={4} md={3} lg={2}>
                        <Card>
                            {isVideoFile(imageUrl) ? (
                                <video
                                    controls
                                    src={imageUrl}
                                    style={{ height: '150px', objectFit: 'cover' }}
                                />
                            ) : isImageFile(imageUrl) ? (
                                <Card.Img 
                                    variant="top" 
                                    src={imageUrl} 
                                    style={{ height: '150px', objectFit: 'cover' }}
                                />
                            ):
                            <iframe
                                src={imageUrl}
                                style={{ height: '150px', width: '100%', border: 'none' }}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="strict-origin-when-cross-origin"
                                title={`embedded-media-${index}`}
                            />
                            
                            }
                            <Card.Body className="p-2">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    className="w-100"
                                    onClick={() => handleRemoveImage(imageUrl)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />

                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <div className="mt-4 d-flex justify-content-end">
                <Button 
                    variant="primary" 
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    ) : (
                        'Save Gallery'
                    )}
                </Button>
            </div>

            <MediaSelector
                isOpen={isMediaSelectorOpen}
                onClose={() => setIsMediaSelectorOpen(false)}
                onSelect={handleMediaSelect}
                isMultiple={true}
            />
        </Container>
    );
};

export default GalleryBuilder;
